import dayjs, { type Dayjs } from 'dayjs';

/**
 * SHAR-1192 — times on the airport's clock.
 *
 * A flight's departure is shown on its ORIGIN airport's clock and its arrival on
 * the DESTINATION's, with the zone's abbreviation, the way the apps show them.
 * The API sends `*Zoned` objects for that (SHAR-1189); an API without them (the
 * staging build before SHAR-1189) gets the old text, so the panel never shows
 * less than it did.
 */
export type Zoned = {
  /** The instant, ISO in UTC. */
  at: string;
  /** The airport's local date, YYYY-MM-DD. */
  localDate: string;
  /** The airport's local time, HH:mm. */
  localTime: string;
  /** "+01:00". */
  utcOffset: string;
  /** IANA zone, "Europe/London". */
  timezone: string;
};

const isZone = (tz: string | null | undefined): tz is string => {
  if (!tz) return false;
  try {
    new Intl.DateTimeFormat('en-GB', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
};

/** "BST", "CEST", "GMT+4"… the zone's short name at that moment; "" if unknown. */
export function zoneAbbr(at: string | number | Date, tz: string | null | undefined): string {
  if (!isZone(tz)) return '';
  const part = new Intl.DateTimeFormat('en-GB', { timeZone: tz, timeZoneName: 'short' })
    .formatToParts(new Date(at))
    .find((p) => p.type === 'timeZoneName');
  return part?.value ?? '';
}

/** The wall clock in `tz` at an instant: { date: 'YYYY-MM-DD', time: 'HH:mm' }. */
export function wallClock(at: string | number | Date, tz: string): { date: string; time: string } {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-GB', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    })
      .formatToParts(new Date(at))
      .map((p) => [p.type, p.value]),
  );
  return { date: `${parts.year}-${parts.month}-${parts.day}`, time: `${parts.hour}:${parts.minute}` };
}

const dmy = (ymd: string) => {
  const [y, m, d] = ymd.split('-');
  return `${d}/${m}/${y}`;
};

/** "01/12/2026 12:00 CET" from a zoned moment; `fallback` when the API sent none. */
export function fmtZoned(z: Zoned | null | undefined, fallback = '—'): string {
  if (!z || !z.localDate || !z.localTime) return fallback;
  const abbr = zoneAbbr(z.at, z.timezone) || (z.utcOffset ? `UTC${z.utcOffset}` : '');
  return `${dmy(z.localDate)} ${z.localTime}${abbr ? ` ${abbr}` : ''}`;
}

/**
 * A time the panel already writes on the airport's clock (a booking's stored
 * departure date and time), with the zone added: "2026-10-09 09:00 BST".
 * Unchanged when the API sent no zoned moment.
 */
export function withAbbr(text: string, z: Zoned | null | undefined): string {
  if (!z) return text;
  const abbr = zoneAbbr(z.at, z.timezone) || (z.utcOffset ? `UTC${z.utcOffset}` : '');
  return abbr ? `${text} ${abbr}` : text;
}

/**
 * The zone of an airport code: the airport just picked in a typeahead when it is
 * that code, else the first known pair [code, zone] for it. Undefined when
 * nothing says (an older API): the caller then uses the browser's clock.
 */
export function zoneForCode(
  code: string | null | undefined,
  picked: { icaoCode?: string | null; iataCode?: string | null; timezone?: string | null } | null | undefined,
  known: [string | null | undefined, string | null | undefined][],
): string | undefined {
  const c = (code ?? '').trim().toUpperCase();
  if (!c) return undefined;
  const same = (x: string | null | undefined) => !!x && x.toUpperCase() === c;
  if (picked && (same(picked.icaoCode) || same(picked.iataCode)) && isZone(picked.timezone)) return picked.timezone;
  for (const [k, tz] of known) if (same(k) && isZone(tz)) return tz;
  return undefined;
}

/**
 * An instant on an airport's clock: "01/12/2026 12:00 CET". Without a zone it
 * is the old text, the browser's own clock and no abbreviation.
 */
export function fmtInstantAt(at: string | Date | null | undefined, tz: string | null | undefined): string {
  if (!at) return '—';
  if (!isZone(tz)) return dayjs(at).format('DD/MM/YYYY HH:mm');
  const w = wallClock(at, tz);
  const abbr = zoneAbbr(at, tz);
  return `${dmy(w.date)} ${w.time}${abbr ? ` ${abbr}` : ''}`;
}

/** UTC offset of `tz` at an instant, in minutes. */
function offsetMinutes(ms: number, tz: string): number {
  const w = wallClock(ms, tz);
  const asUtc = Date.UTC(+w.date.slice(0, 4), +w.date.slice(5, 7) - 1, +w.date.slice(8, 10), +w.time.slice(0, 2), +w.time.slice(3, 5));
  return Math.round((asUtc - Math.floor(ms / 60000) * 60000) / 60000);
}

/**
 * The instant at which `tz`'s clock reads the picker's date and time. The date
 * pickers hand back a Dayjs on the browser's clock; only its fields are used.
 * A time skipped when clocks go forward moves forward by the gap, and a time
 * repeated when they go back is the first of the two — the API's rule
 * (common/airport-time.ts). Without a zone it is the browser's clock, as before.
 */
export function wallToInstant(v: Dayjs, tz: string | null | undefined): string {
  if (!isZone(tz)) return v.second(0).millisecond(0).toISOString();
  const asUtc = Date.UTC(v.year(), v.month(), v.date(), v.hour(), v.minute());
  const want = `${v.format('YYYY-MM-DD')} ${v.format('HH:mm')}`;
  // the offsets in force half a day either side cover any clock change near it
  const before = asUtc - offsetMinutes(asUtc - 12 * 3600_000, tz) * 60000;
  const after = asUtc - offsetMinutes(asUtc + 12 * 3600_000, tz) * 60000;
  const reads = (ms: number) => {
    const w = wallClock(ms, tz);
    return `${w.date} ${w.time}` === want;
  };
  const valid = [before, after].filter(reads).sort((a, b) => a - b);
  // repeated hour: the first; skipped hour: the old offset, i.e. forward by the gap
  return new Date(valid.length ? valid[0] : before).toISOString();
}

/**
 * A Dayjs for a date picker that shows `tz`'s wall clock at an instant (its
 * fields are that wall clock; the browser's zone is irrelevant). Without a zone
 * it is the instant on the browser's clock, as before.
 */
export function instantToWall(at: string | Date, tz: string | null | undefined): Dayjs {
  if (!isZone(tz)) return dayjs(at);
  const w = wallClock(at, tz);
  return dayjs(new Date(+w.date.slice(0, 4), +w.date.slice(5, 7) - 1, +w.date.slice(8, 10), +w.time.slice(0, 2), +w.time.slice(3, 5)));
}

/** "London time (BST)" / "your browser's time (UTC+01:00)" — for the label beside a picker. */
export function clockName(tz: string | null | undefined, city?: string | null): string {
  if (!isZone(tz)) return `your browser's time (UTC${dayjs().format('Z')})`;
  const abbr = zoneAbbr(Date.now(), tz);
  return `${city ? `${city} time` : tz}${abbr ? ` (${abbr})` : ''}`;
}

/** SHAR-1192 — how SHAR-1188 guessed a zone, in words. */
export function guessText(how: string | null | undefined): string {
  if (!how) return 'No source gave this airport a time zone, so it was guessed. Check it.';
  const m = /^nearest(-any)?:(.+)$/.exec(how);
  if (m) return `Guessed from the nearest airport${m[1] ? '' : ' in the same country'} (${m[2]}). Check it.`;
  if (how === 'country') return "Guessed as the country's most common time zone. Check it.";
  return `Guessed (${how}). Check it.`;
}

/**
 * SHAR-1192 (2b) — what to tell the admin after creating an airport whose zone
 * the API guessed, or null. An older API answers without `timezoneGuessed`:
 * nothing to say.
 */
export function createdZoneNotice(row: { timezone?: string | null; timezoneGuessed?: boolean; timezoneGuess?: string | null } | null | undefined): string | null {
  if (!row?.timezoneGuessed) return null;
  return `Time zone set to ${row.timezone ?? 'UTC'}. ${guessText(row.timezoneGuess)}`;
}
